import { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, money, downloadFile, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
const pctCls = (p) => (p < 0 ? "text-red-600" : p < 20 ? "text-amber-600" : "text-emerald-700");

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="num mt-2 text-2xl font-semibold text-[#01195D]" data-testid={testid}>{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

const GpTable = ({ title, rows, showOrders, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white" data-testid={testid}>
    <div className="border-b border-slate-200 px-5 py-3 font-semibold text-[#01195D]">{title}</div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="px-4 py-2.5">Name</th>{showOrders && <th className="px-4 py-2.5 text-right">Orders</th>}<th className="px-4 py-2.5 text-right">Revenue</th><th className="px-4 py-2.5 text-right">Cost</th><th className="px-4 py-2.5 text-right">Gross profit</th><th className="px-4 py-2.5 text-right">GP %</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="row-hover border-t border-slate-100">
              <td className="px-4 py-2.5 font-medium text-[#0B1523]">{r.name}</td>
              {showOrders && <td className="num px-4 py-2.5 text-right">{r.orders}</td>}
              <td className="num px-4 py-2.5 text-right">{money(r.revenue)}</td>
              <td className="num px-4 py-2.5 text-right text-slate-500">{money(r.cost)}</td>
              <td className="num px-4 py-2.5 text-right font-semibold text-[#01195D]">{money(r.gross_profit)}</td>
              <td className={`num px-4 py-2.5 text-right font-semibold ${pctCls(r.gp_pct)}`}>{r.gp_pct.toFixed(1)}%</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No orders this month.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

export const ProfitReport = ({ month }) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (month) api.get("/reports/financial", { params: { month } }).then((r) => setD(r.data)); }, [month]);
  if (!d) return null;
  const exp = (format) =>
    downloadFile(`/reports/financial/export?month=${month}&format=${format}`, `GS_Pacific_Gross_Profit_${month}.${format}`).catch((e) => toast.error(errMsg(e)));
  const t = d.totals;
  return (
    <div className="space-y-6 fade-up" data-testid="profit-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Based on sales orders converted in the selected month. Amounts ex GST.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exp("xlsx")} data-testid="profit-export-xlsx-btn"><FileSpreadsheet className="mr-1 h-4 w-4 text-emerald-700" /> Export Excel</Button>
          <Button variant="outline" onClick={() => exp("pdf")} data-testid="profit-export-pdf-btn"><FileText className="mr-1 h-4 w-4 text-red-600" /> Export PDF</Button>
        </div>
      </div>
      {t.missing_cost_lines > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="profit-missing-cost">
          <AlertTriangle className="h-4 w-4" /> {t.missing_cost_lines} order line(s) have no cost price, so profit may be overstated. Add costs in Items or your inventory upload.
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Revenue (ex GST)" value={money(t.revenue)} sub={`${t.orders} orders`} testid="profit-revenue" />
        <Stat label="Cost of sales" value={money(t.cost)} testid="profit-cost" />
        <Stat label="Gross profit" value={money(t.gross_profit)} testid="profit-gp" />
        <Stat label="GP margin" value={`${t.gp_pct.toFixed(1)}%`} testid="profit-gp-pct" />
        <Stat label="GST collected" value={money(t.gst)} testid="profit-gst" />
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="profit-trend-chart">
        <div className="mb-4 font-semibold text-[#01195D]">Last 12 months</div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={d.trend.map((x) => ({ ...x, label: monthLabel(x.month) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="v" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
            <YAxis yAxisId="p" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip formatter={(v, n) => (n === "GP %" ? `${v}%` : money(v))} />
            <Legend />
            <Bar yAxisId="v" dataKey="revenue" name="Revenue" fill="#1BA3E0" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="v" dataKey="cost" name="Cost" fill="#94A3B8" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="v" dataKey="gross_profit" name="Gross profit" fill="#01195D" radius={[4, 4, 0, 0]} />
            <Line yAxisId="p" dataKey="gp_pct" name="GP %" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <GpTable title="Gross profit by sales rep" rows={d.by_rep} showOrders testid="profit-by-rep" />
        <GpTable title="Gross profit by customer" rows={d.by_customer} showOrders testid="profit-by-customer" />
        <GpTable title="Gross profit by category" rows={d.by_category} testid="profit-by-category" />
        <GpTable title="Top 15 items by gross profit" rows={d.by_item} testid="profit-by-item" />
      </div>
    </div>
  );
};
